import {useCallback, useEffect, useRef} from "react";
import type CombatantTransform from "@/encounter/model/CombatantTransform.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {v4 as UUID} from "uuid";

export function useCombatantTransforms() {
    const {previewTransforms, commitTransforms} = useCombatants();
    const pendingTransformsRef = useRef<CombatantTransform[] | undefined>(undefined);
    const transformFrameRef = useRef<number | undefined>(undefined);
    const interactionIdRef = useRef<string | undefined>(undefined);
    const sequenceRef = useRef(0);

    useEffect(() => () => {
        if (transformFrameRef.current !== undefined) {
            window.cancelAnimationFrame(transformFrameRef.current);
        }
    }, []);

    const nextBatch = useCallback((transforms: CombatantTransform[]) => ({
        interactionId: interactionIdRef.current ??= UUID(),
        sequence: ++sequenceRef.current,
        transforms,
    }), []);

    const previewCombatantTransforms = useCallback((transforms: CombatantTransform[]) => {
        pendingTransformsRef.current = transforms;
        if (transformFrameRef.current !== undefined) return;

        transformFrameRef.current = window.requestAnimationFrame(() => {
            transformFrameRef.current = undefined;
            const pendingTransforms = pendingTransformsRef.current;
            pendingTransformsRef.current = undefined;
            if (pendingTransforms) previewTransforms(nextBatch(pendingTransforms));
        });
    }, [nextBatch, previewTransforms]);

    const commitCombatantTransforms = useCallback((transforms: CombatantTransform[]) => {
        if (transformFrameRef.current !== undefined) {
            window.cancelAnimationFrame(transformFrameRef.current);
            transformFrameRef.current = undefined;
        }
        pendingTransformsRef.current = undefined;
        const batch = nextBatch(transforms);
        interactionIdRef.current = undefined;
        void commitTransforms(batch);
    }, [commitTransforms, nextBatch]);

    return {previewCombatantTransforms, commitCombatantTransforms};
}
